import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, eur } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Check } from "lucide-react";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [planKey, setPlanKey] = useState(params.get("plan") || "starter");
  const [company, setCompany] = useState({
    company_name: "", address: "", zip: "", city: "", country: "Deutschland",
    phone: "", email: "", website: "", ust_id: "", tax_number: "", company_type: "",
    bank_iban: "", bank_bic: "",
  });
  const [user, setUser] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/plans").then((r) => setPlans(r.data.filter((p) => !p.enterprise))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", { company: { ...company, email: company.email || user.email }, user, plan_key: planKey });
      toast.success("Konto erstellt. Ihre 14-tägige Testphase hat begonnen.");
      navigate("/app", { replace: true });
      window.location.reload();
    } catch (err) {
      toast.error(apiError(err, "Registrierung fehlgeschlagen"));
    }
    setLoading(false);
  };

  const fc = (k) => (e) => setCompany({ ...company, [k]: e.target.value });
  const fu = (k) => (e) => setUser({ ...user, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="register-page">
      <header className="border-b border-zinc-200 bg-white/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-zinc-900 rounded-lg flex items-center justify-center"><span className="text-white text-[10px] font-bold font-mono">L19</span></div>
            <span className="font-display font-semibold text-lg">L<span className="text-zinc-400">19</span></span>
          </Link>
          <Link to="/login" className="text-sm text-zinc-600 hover:text-zinc-900" data-testid="register-login-link">Bereits registriert? Einloggen</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 py-10">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Unternehmen registrieren</h1>
        <p className="text-sm text-zinc-500 mt-2">14 Tage kostenlos testen. Keine Zahlungsdaten erforderlich. Monatlich kündbar.</p>
        <form onSubmit={submit} className="mt-10 space-y-8" data-testid="register-form">
          <section className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display font-medium text-lg">Unternehmen</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Firmenname *</Label><Input required value={company.company_name} onChange={fc("company_name")} data-testid="reg-company-name" className="mt-1.5" /></div>
              <div><Label>Straße & Nr.</Label><Input value={company.address} onChange={fc("address")} data-testid="reg-company-address" className="mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>PLZ</Label><Input value={company.zip} onChange={fc("zip")} data-testid="reg-company-zip" className="mt-1.5" /></div>
                <div><Label>Ort</Label><Input value={company.city} onChange={fc("city")} data-testid="reg-company-city" className="mt-1.5" /></div>
              </div>
              <div><Label>Land</Label><Input value={company.country} onChange={fc("country")} data-testid="reg-company-country" className="mt-1.5" /></div>
              <div><Label>Telefon</Label><Input value={company.phone} onChange={fc("phone")} data-testid="reg-company-phone" className="mt-1.5" /></div>
              <div><Label>Geschäfts-E-Mail</Label><Input type="email" value={company.email} onChange={fc("email")} placeholder="(sonst Login-E-Mail)" data-testid="reg-company-email" className="mt-1.5" /></div>
              <div><Label>Website</Label><Input value={company.website} onChange={fc("website")} data-testid="reg-company-website" className="mt-1.5" /></div>
              <div><Label>USt-IdNr.</Label><Input value={company.ust_id} onChange={fc("ust_id")} placeholder="DE123456789" data-testid="reg-company-ustid" className="mt-1.5" /></div>
              <div><Label>Steuernummer</Label><Input value={company.tax_number} onChange={fc("tax_number")} data-testid="reg-company-taxnumber" className="mt-1.5" /></div>
              <div><Label>Unternehmensart</Label><Input value={company.company_type} onChange={fc("company_type")} placeholder="z. B. Einzelunternehmen, GmbH" data-testid="reg-company-type" className="mt-1.5" /></div>
              <div><Label>IBAN</Label><Input value={company.bank_iban} onChange={fc("bank_iban")} data-testid="reg-company-iban" className="mt-1.5" /></div>
            </div>
          </section>

          <section className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display font-medium text-lg">Benutzer (Administrator)</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Vorname *</Label><Input required value={user.first_name} onChange={fu("first_name")} data-testid="reg-user-firstname" className="mt-1.5" /></div>
              <div><Label>Nachname *</Label><Input required value={user.last_name} onChange={fu("last_name")} data-testid="reg-user-lastname" className="mt-1.5" /></div>
              <div><Label>E-Mail *</Label><Input type="email" required value={user.email} onChange={fu("email")} data-testid="reg-user-email" className="mt-1.5" /></div>
              <div><Label>Passwort * (min. 8 Zeichen)</Label><Input type="password" required minLength={8} value={user.password} onChange={fu("password")} data-testid="reg-user-password" className="mt-1.5" /></div>
            </div>
          </section>

          <section className="bg-white border border-zinc-200 rounded-xl p-6 shadow-sm">
            <h2 className="font-display font-medium text-lg">Tarif wählen</h2>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {plans.map((p) => (
                <button type="button" key={p.key} onClick={() => setPlanKey(p.key)} data-testid={`reg-plan-${p.key}`}
                  className={`text-left border rounded-xl p-5 transition-colors ${planKey === p.key ? "border-zinc-900 ring-1 ring-zinc-900" : "border-zinc-200 hover:border-zinc-400"}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-mono text-xs uppercase tracking-wider text-zinc-500">{p.name}</span>
                    {planKey === p.key && <Check className="w-4 h-4" />}
                  </div>
                  <div className="font-display text-2xl font-bold mt-2">{eur(p.price_monthly)}<span className="text-sm font-normal text-zinc-500">/Monat</span></div>
                  <div className="text-xs text-zinc-500 mt-2">{p.features?.slice(0, 3).join(" · ")}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-4">Die Zahlungsmethode hinterlegen Sie nach der Testphase unter „Abo & Abrechnung“.</p>
          </section>

          <Button type="submit" disabled={loading} data-testid="register-submit-button"
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white py-6 text-base">
            {loading ? "Konto wird erstellt…" : "Jetzt registrieren & testen"}
          </Button>
        </form>
      </main>
    </div>
  );
}
